import { STEP_TYPES, getAllowedNextTypesFor } from "./transportConstants";

const formatEur = new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format;

/* ── Time helpers ──────────────────────────────────────────────────── */

/**
 * Converts a Unix-ms timestamp (or numeric string) to a datetime-local
 * string suitable for <input type="datetime-local">.
 */
export const toDatetimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(typeof value === "number" ? value : Number(value));
  if (isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    date.getFullYear() + "-" +
    pad(date.getMonth() + 1) + "-" +
    pad(date.getDate()) + "T" +
    pad(date.getHours()) + ":" +
    pad(date.getMinutes())
  );
};

/* ── Default step factory ──────────────────────────────────────────── */

/**
 * Creates a default step of the given type.
 *
 * Prefill rules:
 *   - First step: departure = origin, departureTime = pickupTime (or now+2h)
 *   - Subsequent steps: departure/departureTime inherited from previous step's arrival
 *   - arrivalTime defaults to now+5h for the very first step,
 *     otherwise now+3h after the previous step's arrival
 */
export const createDefaultStep = (type, previousSteps = [], { origin, pickupTime } = {}) => {
  const isFirst = previousSteps.length === 0;
  const lastStep = previousSteps.length > 0 ? previousSteps[previousSteps.length - 1] : null;

  const now = Date.now();
  const defaultDepartureTime = toDatetimeLocal(now + 2 * 60 * 60 * 1000);
  const defaultArrivalTime   = toDatetimeLocal(now + 5 * 60 * 60 * 1000);

  const base = {
    type,
    departure: isFirst ? (origin || "") : (lastStep?.arrival || ""),
    arrival: "",
    departureTime: isFirst
      ? (pickupTime ? toDatetimeLocal(pickupTime) : defaultDepartureTime)
      : (lastStep?.arrivalTime || defaultDepartureTime),
    arrivalTime: defaultArrivalTime,
  };

  if (type === STEP_TYPES.BUS) {
    return { ...base, busCompany: "", busLine: "" };
  }

  if (type === STEP_TYPES.WAIT) {
    const lastBus = [...previousSteps].reverse().find((s) => s.type === STEP_TYPES.BUS);
    return {
      ...base,
      inboundBusCompany:  lastBus?.busCompany || "",
      inboundBusLine:     lastBus?.busLine    || "",
      outboundBusCompany: "",
      outboundBusLine:    "",
    };
  }

  // CAR
  return base;
};

/* ── Chain validation ──────────────────────────────────────────────── */

/**
 * After a reorder or removal, trims any steps that violate the
 * allowed-sequence rules from the first broken position onward.
 */
export const validateChain = (steps) => {
  if (steps.length <= 1) return steps;
  const valid = [steps[0]];

  for (let i = 1; i < steps.length; i++) {
    const prev    = valid[valid.length - 1];
    const curr    = steps[i];
    const allowed = getAllowedNextTypesFor(prev.type);
    if (allowed.includes(curr.type)) valid.push(curr);
    // otherwise: silently drop the invalid step
  }

  return valid;
};

/* ── Step sync helpers ─────────────────────────────────────────────── */

/**
 * When a step at `index` changes, propagate relevant fields to adjacent steps:
 *   - Bus arrival → following Wait's departure & inbound bus info
 *   - Wait arrivalTime → following Bus's departureTime & departure
 */
export const syncAdjacentSteps = (steps, index, updatedStep) => {
  const next = steps.map((s, i) => (i === index ? updatedStep : s));

  const following = next[index + 1];

  if (following?.type === STEP_TYPES.WAIT && updatedStep.type === STEP_TYPES.BUS) {
    next[index + 1] = {
      ...following,
      departure:          updatedStep.arrival      || following.departure,
      departureTime:      updatedStep.arrivalTime  || following.departureTime,
      inboundBusCompany:  updatedStep.busCompany   || following.inboundBusCompany,
      inboundBusLine:     updatedStep.busLine      || following.inboundBusLine,
    };
  }

  if (updatedStep.type === STEP_TYPES.WAIT) {
    const nextBus = next[index + 1];
    if (nextBus?.type === STEP_TYPES.BUS) {
      next[index + 1] = {
        ...nextBus,
        departure:     updatedStep.departure    || nextBus.departure,
        departureTime: updatedStep.arrivalTime  || nextBus.departureTime,
      };
    }
  }

  return next;
};